export type EffortValue = 'low' | 'medium' | 'high';

export interface EffortCapability {
  supportsEffort: boolean;
  effortOptions: EffortValue[];
  defaultEffort: EffortValue;
}

const OPENAI_REASONING_PREFIXES = ['o1', 'o3', 'o4', 'gpt-5'];

const ANTHROPIC_ADAPTIVE_FRAGMENTS = [
  'claude-opus-4-6',
  'claude-opus-4-7',
  'claude-opus-4-8',
  'claude-sonnet-4-6',
  'claude-sonnet-4-7',
  'claude-haiku-4-6',
  'claude-haiku-4-7',
  'claude-fable-5',
];

const XAI_REASONING_FRAGMENTS = ['grok-3-mini', 'grok-4'];

const GROQ_REASONING_FRAGMENTS = ['gpt-oss', 'qwen-qwq', 'deepseek-r1'];

const DEFAULT_EFFORT: EffortValue = 'low';

const STANDARD_EFFORT_OPTIONS: EffortValue[] = ['low', 'medium', 'high'];

const XAI_EFFORT_OPTIONS: EffortValue[] = ['low', 'high'];

const NO_EFFORT: EffortCapability = {
  supportsEffort: false,
  effortOptions: [],
  defaultEffort: DEFAULT_EFFORT,
};

function matchesAny(model: string, fragments: string[]): boolean {
  const normalized = (model ?? '').toLowerCase();
  return fragments.some(fragment => normalized.includes(fragment));
}

function getOpenAiEffortCapability(model: string): EffortCapability {
  if (!matchesAny(model, OPENAI_REASONING_PREFIXES)) return NO_EFFORT;
  return {
    supportsEffort: true,
    effortOptions: STANDARD_EFFORT_OPTIONS,
    defaultEffort: DEFAULT_EFFORT,
  };
}

function getAnthropicEffortCapability(model: string): EffortCapability {
  if (!matchesAny(model, ANTHROPIC_ADAPTIVE_FRAGMENTS)) return NO_EFFORT;
  return {
    supportsEffort: true,
    effortOptions: STANDARD_EFFORT_OPTIONS,
    defaultEffort: DEFAULT_EFFORT,
  };
}

function getXaiEffortCapability(model: string): EffortCapability {
  if (!matchesAny(model, XAI_REASONING_FRAGMENTS)) return NO_EFFORT;
  return {
    supportsEffort: true,
    effortOptions: XAI_EFFORT_OPTIONS,
    defaultEffort: DEFAULT_EFFORT,
  };
}

function getGroqEffortCapability(model: string): EffortCapability {
  if (!matchesAny(model, GROQ_REASONING_FRAGMENTS)) return NO_EFFORT;
  return {
    supportsEffort: true,
    effortOptions: STANDARD_EFFORT_OPTIONS,
    defaultEffort: DEFAULT_EFFORT,
  };
}

export function getEffortCapability(
  aiSystem: string,
  aiModel: string,
): EffortCapability {
  if (!aiModel) return NO_EFFORT;
  const system = (aiSystem ?? '').toLowerCase();
  if (system === 'openai') return getOpenAiEffortCapability(aiModel);
  if (system === 'anthropic') return getAnthropicEffortCapability(aiModel);
  if (system === 'xai') return getXaiEffortCapability(aiModel);
  if (system === 'groq') return getGroqEffortCapability(aiModel);
  return NO_EFFORT;
}
